<template>
  <v-dialog
    content-class="license-activation-modal"
    :value="visible"
    :max-width="520"
    @click:outside="$emit('close')"
    @keydown.esc="$emit('close')"
  >
    <v-card class="license-activation-modal__card">
      <div class="license-activation-modal__header">
        <span class="license-activation-modal__title">
          {{ $t('licensing.settings.activate.modal.title') }}
        </span>
        <button
          class="license-activation-modal__close"
          aria-label="Close"
          @click="$emit('close')"
        >
          <v-icon small>close</v-icon>
        </button>
      </div>

      <div class="license-activation-modal__body">
        <p class="license-activation-modal__description">
          {{ orgId
            ? $t('licensing.settings.activate.modal.desc.org')
            : $t('licensing.settings.activate.modal.desc.tenant')
          }}
        </p>

        <span class="license-activation-modal__label">
          {{ $t('licensing.settings.activate.modal.label') }}
        </span>

        <label
          v-if="!fileName"
          class="license-activation-modal__dropzone"
          :class="{ 'license-activation-modal__dropzone--active': dragging }"
          @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="onDrop"
        >
          <input
            ref="fileInput"
            type="file"
            accept=".lic,.license,.bin,.enc"
            class="license-activation-modal__file-input"
            :disabled="submitting"
            @change="onFileSelect"
          >
          <v-icon small color="grey">upload_file</v-icon>
          <span class="license-activation-modal__dropzone-text">
            {{ $t('licensing.settings.activate.modal.dropzone') }}
          </span>
        </label>
        <div
          v-else
          class="license-activation-modal__dropzone license-activation-modal__dropzone--has-file"
        >
          <v-icon small color="success">check_circle</v-icon>
          <span class="license-activation-modal__file-name">{{ fileName }}</span>
          <button
            class="license-activation-modal__file-remove"
            aria-label="Remove file"
            @click="clearFile"
          >
            <v-icon x-small>close</v-icon>
          </button>
        </div>

        <div
          v-if="errorMessage"
          class="license-activation-modal__error"
          role="alert"
        >
          <v-icon small color="error">error_outline</v-icon>
          {{ errorMessage }}
        </div>
      </div>

      <div class="license-activation-modal__footer">
        <v-btn
          text
          :disabled="submitting"
          @click="$emit('close')"
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="submitting"
          :disabled="!licenseBase64"
          @click="submit"
        >
          {{ $t('licensing.settings.activate.modal.submit') }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { activateLicense, activateOrgLicense } from '@/services/repository';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB

export default {
  name: 'LicenseActivationModal',
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    orgId: {
      type: String,
      default: null,
    },
    isMssp: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      licenseBase64: '',
      fileName: '',
      dragging: false,
      submitting: false,
      errorMessage: '',
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.clearFile();
        this.errorMessage = '';
      }
    },
  },
  methods: {
    onFileSelect(event) {
      const file = event.target.files && event.target.files[0];
      if (file) this.readFile(file);
    },
    onDrop(event) {
      this.dragging = false;
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) this.readFile(file);
    },
    readFile(file) {
      this.errorMessage = '';
      if (file.size > MAX_FILE_SIZE) {
        this.errorMessage = this.$t('licensing.settings.activate.modal.error.size');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        // .lic files already contain base64 text — read as-is, just trim whitespace
        this.licenseBase64 = (reader.result || '').trim();
        this.fileName = file.name;
      };
      reader.onerror = () => {
        this.errorMessage = this.$t('licensing.settings.activate.modal.error.read');
      };
      reader.readAsText(file);
    },
    clearFile() {
      this.licenseBase64 = '';
      this.fileName = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    async submit() {
      this.errorMessage = '';
      this.submitting = true;
      try {
        const payload = { data: this.licenseBase64 };
        if (this.orgId) {
          await activateOrgLicense(this.orgId, payload);
        } else {
          await activateLicense(payload);
        }
        this.$emit('activated');
      } catch (err) {
        const msg = err?.message || err?.response?.data?.message;
        this.errorMessage = msg || this.$t('licensing.settings.activate.modal.error.generic');
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/_sizing";
@import "@/assets/styles/_colors";
@import "@/assets/styles/_semantic-sizing";

.license-activation-modal__card {
  border-radius: $radius-lg;
  overflow: hidden;
}

.license-activation-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $space-4 $space-5;
  border-bottom: $border-width-thin solid var(--border-primary);
}

.license-activation-modal__title {
  font-family: $font-family-bold;
  font-size: $font-size-base;
  color: var(--text-primary);
}

.license-activation-modal__close {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  padding: $space-1;
  border-radius: $radius-6;

  &:hover { background: var(--bg-tertiary, $gray-100); }
  &:focus-visible { outline: $border-width-medium solid var(--brand-primary); }
}

.license-activation-modal__body {
  padding: $space-4 $space-5;
}

.license-activation-modal__description {
  color: var(--text-secondary);
  font-family: $font-family-regular;
  font-size: $font-size-sm;
  margin-bottom: $space-4;
  line-height: $line-height-relaxed;
}

.license-activation-modal__label {
  display: block;
  color: var(--text-primary);
  font-family: $font-family-bold;
  font-size: $font-size-sm;
  margin-bottom: $space-2;
}

.license-activation-modal__file-input {
  display: none;
}

.license-activation-modal__dropzone {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $space-2;
  padding: $space-6 $space-4;
  border: $border-width-medium dashed var(--border-primary);
  border-radius: $radius-6;
  background: var(--bg-primary);
  cursor: pointer;
  transition: border-color $transition-duration-fast $transition-timing-ease,
    background $transition-duration-fast $transition-timing-ease;

  &:hover {
    border-color: var(--brand-primary);
    background: var(--bg-tertiary, $gray-100);
  }

  &--active {
    border-color: var(--brand-primary);
    background: var(--bg-tertiary, $gray-100);
  }

  &--has-file {
    border-style: solid;
    padding: $space-3 $space-4;
    justify-content: flex-start;
  }
}

.license-activation-modal__dropzone-text {
  font-family: $font-family-regular;
  font-size: $font-size-sm;
  color: var(--text-secondary);
}

.license-activation-modal__file-name {
  font-family: $font-family-bold;
  font-size: $font-size-sm;
  color: var(--text-primary);
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.license-activation-modal__file-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  padding: $space-1;
  border-radius: $radius-full;
  flex-shrink: 0;

  &:hover { background: var(--bg-tertiary, $gray-100); }
  &:focus-visible { outline: $border-width-medium solid var(--brand-primary); }
}

.license-activation-modal__error {
  display: flex;
  align-items: center;
  gap: $space-2;
  margin-top: $space-3;
  padding: $space-2-5 $space-3;
  background: $error-50;
  color: $error-500;
  border-radius: $radius-6;
  font-family: $font-family-regular;
  font-size: $font-size-sm;
}

.license-activation-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: $space-2;
  padding: $space-3 $space-5 $space-4;
  border-top: $border-width-thin solid var(--border-primary);
}
</style>
